
import React from 'react';
import { 
  AddOrEditItemRequestSchema,
  BulkUploadItemAddedByEnum,
} from '../../client-sdk'; 
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStoreItemApi } from '../../Hooks/useStoreItemApi';
import { useSnackbar } from '../stateless/SnackBarProvider';


export const useAddStoreItem = () => {
  const queryClient = useQueryClient();
  const { addStoreItem } = useStoreItemApi();
  const { showMessage } = useSnackbar();
  return useMutation({
    mutationKey: ['CreateStoreItem'],
    mutationFn: (data: AddOrEditItemRequestSchema) => addStoreItem(data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store item added successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to add Store item',
        'error'
      );
    },
  });
};

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  storeId?: string;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
};

export const useGetAllStoreItem = (queryParams: Params) => {

  const { getAllStoreItem } = useStoreItemApi();
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const localStorageMerchantId = userData?.userId;

  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: [
      'getStoreItem',
      queryParams
    ],
    queryFn: () => {
      // Always inject merchantId from localStorage if not present
      return getAllStoreItem({
        ...queryParams,
        merchantId: localStorageMerchantId,
      });
    },
    retry: false,
  });

  return {
    data,
    isLoading:isFetching || isRefetching,
    refetch
  };
};

export const useBlockUnblockStoreItemById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockOrUnblockStoreItemById } = useStoreItemApi();

  return useMutation({
    mutationKey: ['blockUnblockStoreItem'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockOrUnblockStoreItemById({ id, status: data.status }),

    onSuccess: (_response, _variables) => {
      const status = _variables.data.status;
      const action = status === 'blocked' ? 'blocked' : 'unblocked';
      showMessage(`Store Item ${action} successfully`, 'success');

      queryClient.invalidateQueries({
        queryKey: ['StoreItemBlock/Unblock'],
        exact: false,
      });
    },

    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update Store item status',
        'error'
      );
    },
  });
};

export const useDeleteStoreItemById = () => {
  const queryClient = useQueryClient();
  const { deleteStoreItemById } = useStoreItemApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteStoreItem'],

    mutationFn: (ids: string[]) => {
      return deleteStoreItemById(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store item deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['deleteStoreItem'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Store item',
        'error'
      );
    },
  });
};

export const useEditStoreItem = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editStoreItem } = useStoreItemApi();

  return useMutation({
    mutationKey: ['editstoreitem'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editStoreItem(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store Item Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the SubAdmin',
        'error'
      );
    },
  });
};

type BulkUploadParams = {
  file: File;
  addedBy: BulkUploadItemAddedByEnum;
  merchantId?: string;
};

export const useBulkUploadItems = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { bulkUploadItems } = useStoreItemApi();

  return useMutation({
    mutationKey: ['bulkUploadItems'],
    mutationFn: ({ file, addedBy, merchantId }: BulkUploadParams) =>
      bulkUploadItems(file, addedBy, merchantId),
    onSuccess: (response: any) => {
      // If jobId exists, it's a background job (>50 records)
      if (response?.jobId) {
        showMessage(
          response?.message || 
          `Bulk upload started. Job ID: ${response.jobId}. Processing ${response.estimatedRows || 0} records in the background.`,
          'info'
        );
      } else {
        // Synchronous processing (<=50 records)
        showMessage(
          response?.message || 
          `Successfully uploaded ${response?.itemsCreated || 0} items`,
          'success'
        );
        queryClient.invalidateQueries({
          queryKey: ['getStoreItem'],
          exact: false,
        });
      }
    },
    onError: (error: any) => {
      // Enhanced error handling to show all error messages from API
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        // PRIORITY 1: If there are errors in the errors array, show them ALL
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          // Format all error messages for display
          const errorMessages = errorData.errors.map((err: any) => {
            const errorRow = err?.row || 'N/A';
            const errorField = err?.field || 'Field';
            const errorMessage = err?.message || 'Validation error';
            return `Row ${errorRow} - ${errorField}: ${errorMessage}`;
          });
          
          // Show ALL error messages in the toast, separated by newlines
          const allErrorsMessage = errorMessages.length > 1
            ? `Validation Errors (${errorMessages.length}):\n\n${errorMessages.join('\n')}`
            : errorMessages[0];
          
          showMessage(allErrorsMessage, 'error');
          return; // Don't show a generic error message
        } 
        // PRIORITY 2: Handle detail array
        else if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const detailMessages = errorData.detail.map((d: any) => 
              d.message || d.msg || JSON.stringify(d)
            ).join('; ');
            showMessage(detailMessages, 'error');
            return;
          } else if (typeof errorData.detail === 'string') {
            showMessage(errorData.detail, 'error');
            return;
          }
        } 
        // PRIORITY 3: Handle message field
        else if (errorData.message) {
          showMessage(errorData.message, 'error');
          return;
        } 
        // PRIORITY 4: Handle error field (but skip generic "Validation failed")
        else if (errorData.error && errorData.error !== 'Validation failed') {
          showMessage(errorData.error, 'error');
          return;
        } 
        // PRIORITY 5: If error is "Validation failed" but no errors array, show generic message
        else if (errorData.error === 'Validation failed') {
          showMessage('Validation failed. Please check the error details in the modal.', 'error');
          return;
        }
        // PRIORITY 6: Handle string response
        else if (typeof errorData === 'string') {
          showMessage(errorData, 'error');
          return;
        }
      }
      
      // Fallback error message
      console.error('Bulk upload error (fallback):', error);
      showMessage(error?.message || 'Failed to upload items', 'error');
    },
  });
};

type BulkUploadStatusParams = {
  jobId: string;
  enabled?: boolean;
  refetchInterval?: number;
};

export const useGetBulkUploadStatus = ({
  jobId,
  enabled = true,
  refetchInterval,
}: BulkUploadStatusParams) => {
  const { getBulkUploadStatus } = useStoreItemApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const queryResult = useQuery({
    queryKey: ['getBulkUploadStatus', jobId],
    queryFn: () => getBulkUploadStatus(jobId),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 2 seconds if job is still processing, otherwise stop polling
      const status = data?.status?.toLowerCase();
      if (status === 'queued' || status === 'processing') {
        return refetchInterval || 2000;
      }
      return false;
    },
    retry: false,
  });

  // Handle status changes using useEffect
  React.useEffect(() => {
    const data = queryResult.data;
    if (!data) return;

    const status = data.status?.toLowerCase();
    
    // Show completion message when job is completed
    if (status === 'completed') {
      showMessage(
        data?.result?.message || 
        `Bulk upload completed. ${data?.result?.itemsCreated || 0} items created successfully.`,
        'success'
      );
      // Invalidate store items query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    } else if (status === 'failed') {
      showMessage(
        'Bulk upload failed. Please check the errors and try again.',
        'error'
      );
    }
  }, [queryResult.data, showMessage, queryClient]);

  return queryResult;
};

export const useDownloadBulkUploadTemplate = () => {
  const { showMessage } = useSnackbar();
  const { downloadBulkUploadTemplate } = useStoreItemApi();

  return useMutation({
    mutationKey: ['downloadBulkUploadTemplate'],
    mutationFn: () => downloadBulkUploadTemplate(),
    onSuccess: (blob: Blob) => {
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // API currently supports only XLSX format for the template
      link.download = 'bulk_upload_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showMessage('Template downloaded successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to download template',
        'error'
      );
    },
  });
};
